// CustomerDialog.tsx
// Reusable Customer Dialog Component for Creating/Editing Customers

import React, { useState } from 'react';
import { adminService, CustomerAddress } from '../../services/adminService';
import '../../styles/CustomerDialog.css';

// Customer interface for the dialog
export interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName?: string;
  isVip: boolean;
  addresses?: CustomerAddress[];
}

interface CustomerDialogProps {
  onClose: () => void;
  onSave: (customer: Customer) => void;
  customer: Customer | null; // null = new customer
}

interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  isVip: boolean;
  addresses?: CustomerAddress[];
}

export const CustomerDialog: React.FC<CustomerDialogProps> = ({
  onClose,
  onSave,
  customer
}) => {
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: customer?.firstName || '',
    lastName: customer?.lastName || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    companyName: customer?.companyName || '',
    isVip: customer?.isVip || false,
    addresses: customer?.addresses || []
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      let savedCustomer: any;

      // Prepare customer data without addresses for now (simplified)
      const customerData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.companyName || '',
        isVip: formData.isVip
      };

      if (customer) {
        // Update existing customer
        savedCustomer = await adminService.updateCustomer(customer.id, customerData);
      } else {
        // Create new customer
        savedCustomer = await adminService.createCustomer(customerData);
      }

      // Transform response to Customer format for consistency
      const transformedCustomer: Customer = {
        id: savedCustomer.id || savedCustomer.Id,
        firstName: savedCustomer.firstName || savedCustomer.FirstName,
        lastName: savedCustomer.lastName || savedCustomer.LastName,
        email: savedCustomer.email || savedCustomer.Email,
        phone: savedCustomer.phone || savedCustomer.Phone,
        companyName: savedCustomer.companyName || savedCustomer.CompanyName,
        isVip: savedCustomer.isVip || savedCustomer.IsVip || false,
        addresses: savedCustomer.addresses || savedCustomer.Addresses || []
      };

      onSave(transformedCustomer);
    } catch (err: any) {
      console.error('Error saving customer:', err);
      setError(err.message || 'Fehler beim Speichern des Kunden');
      setSaving(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{customer ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h2>
          <button className="close-btn" onClick={onClose} type="button">
            &times;
          </button>
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="customer-form">
          <div className="form-row">
            <div className="form-group">
              <label>Vorname *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Nachname *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label>Telefon *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label>Firma</label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              disabled={saving}
            />
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.isVip}
                onChange={(e) => setFormData({ ...formData, isVip: e.target.checked })}
                disabled={saving}
              />
              VIP-Kunde
            </label>
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={onClose} disabled={saving} className="btn-cancel">
              Abbrechen
            </button>
            <button type="submit" disabled={saving} className="btn-submit">
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
